import { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stored API key for Authorization header (used by Connect Existing flow AND session fallback)
let storedApiKey = '';

// Helper: fetch with credentials (cookies sent automatically) + API key in Authorization header as fallback
const authFetch = (url, options = {}) => {
  const headers = { ...options.headers };
  // Always include API key in Authorization header when available (cookie + header = double insurance)
  const effectiveKey = storedApiKey && storedApiKey !== 'session' ? storedApiKey : sessionStorage.getItem('dsk_key') || '';
  if (effectiveKey) {
    headers['Authorization'] = `Bearer ${effectiveKey}`;
  }
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });
};

// Function to update the stored API key (called when apiKey state changes)
const setStoredApiKey = (key) => {
  storedApiKey = key || '';
  // Persist raw key in sessionStorage for Authorization header fallback (survives page refresh within tab)
  if (key && key !== 'session') {
    sessionStorage.setItem('dsk_key', key);
  }
};

// Secure Storage: localStorage for display prefs only (label, email)
const secureStorage = {
  getPref: (key) => localStorage.getItem(key) || '',
  setPref: (key, val) => val ? localStorage.setItem(key, val) : localStorage.removeItem(key),
  removePref: (key) => localStorage.removeItem(key),
};

export function useAuth() {
  const [apiKeyState, setApiKeyState] = useState('');
  const [apiKeyLabel, setApiKeyLabel] = useState(secureStorage.getPref('designsync_api_label'));
  const [isKeyRevealed, setIsKeyRevealed] = useState(false);
  const [keyLoading, setKeyLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [userEmail, setUserEmail] = useState(secureStorage.getPref('designsync_user_email'));
  const [emailInput, setEmailInput] = useState('');
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [connectKeyInput, setConnectKeyInput] = useState('');
  const [showConnectKey, setShowConnectKey] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  // Wrapper to update both React state and module-level stored key
  const setApiKey = useCallback((key) => {
    setApiKeyState(key);
    setStoredApiKey(key);
  }, []);

  // Expose apiKeyState as apiKey for external use
  const apiKey = apiKeyState;

  const clearApiKey = useCallback(async () => {
    setApiKey(''); setApiKeyLabel(''); setUserEmail('');
    sessionStorage.removeItem('dsk_key');
    secureStorage.removePref('designsync_api_label');
    secureStorage.removePref('designsync_user_email');
    try { await authFetch(`${API_URL}/api/keys/logout`, { method: 'POST' }); } catch (e) { console.error('Logout request failed (best-effort):', e.message); }
  }, [setApiKey]);

  const validateSession = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/keys/validate`);
      if (res.ok) {
        const data = await res.json();
        if (data.valid) {
          // Use raw key from sessionStorage if available; fall back to 'session' sentinel for cookie-only
          const savedKey = sessionStorage.getItem('dsk_key') || 'session';
          setApiKey(savedKey);
          setApiKeyLabel(data.label || '');
          setUserEmail(data.email || '');
          secureStorage.setPref('designsync_api_label', data.label || '');
          secureStorage.setPref('designsync_user_email', data.email || '');
          return;
        }
      }
      clearApiKey();
    } catch (err) { console.error('Session validation failed — user needs to log in'); }
  }, [clearApiKey]);

  const validateApiKey = useCallback(async (key) => {
    try {
      const res = await authFetch(`${API_URL}/api/keys/validate`);
      if (res.ok) {
        const data = await res.json();
        if (data.valid) {
          setApiKey(key);
          setApiKeyLabel(data.label || '');
          setUserEmail(data.email || '');
          secureStorage.setPref('designsync_api_label', data.label || '');
          secureStorage.setPref('designsync_user_email', data.email || '');
          return;
        }
      }
      clearApiKey();
    } catch (err) { console.error('API key validation failed:', err.message); }
  }, [clearApiKey]);

  const maskKey = (k) => !k ? '' : k.length <= 12 ? '****' : k.substring(0, 8) + '****' + k.substring(k.length - 4);

  const copyApiKey = async () => {
    try { await navigator.clipboard.writeText(apiKey); setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); } catch (err) { console.error('Clipboard copy failed:', err); }
  };

  const generateApiKey = async (email) => {
    if (!email) return;
    setKeyLoading(true); setAuthMessage('');
    try {
      const res = await authFetch(`${API_URL}/api/keys/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const r = await res.json();
      if (res.ok && r.success) {
        setOtpEmail(email);
        setShowOtpPrompt(true);
        setShowEmailPrompt(false);
        setAuthMessage(r.message || 'Check your email for a verification code.');
      } else {
        setAuthMessage(r.error || 'Failed to send verification code.');
      }
    } catch (err) { console.error('Failed to request OTP:', err); setAuthMessage('Network error.'); } finally { setKeyLoading(false); }
  };

  const verifyOtp = async () => {
    if (!otpEmail || !otpInput) return;
    setKeyLoading(true); setAuthMessage('');
    try {
      const res = await authFetch(`${API_URL}/api/keys/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail, otp: otpInput, remember_me: rememberMe })
      });
      if (res.ok) {
        const r = await res.json();
        if (r.api_key) {
          setApiKey(r.api_key); setApiKeyLabel(r.label || ''); setUserEmail(otpEmail);
          secureStorage.setPref('designsync_api_label', r.label || '');
          secureStorage.setPref('designsync_user_email', otpEmail);
          setShowOtpPrompt(false); setOtpInput(''); setOtpEmail('');
          setAuthMessage('');
        }
      } else {
        const err = await res.json();
        setAuthMessage(err.error || 'Invalid verification code.');
      }
    } catch (err) { console.error('OTP verification failed:', err); setAuthMessage('Verification failed.'); } finally { setKeyLoading(false); }
  };

  const regenerateApiKey = async () => {
    if (!apiKey) return;
    setKeyLoading(true); setShowRegenerateConfirm(false);
    try {
      const payload = apiKey !== 'session' ? { current_key: apiKey } : {};
      const res = await authFetch(`${API_URL}/api/keys/regenerate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const r = await res.json();
        setApiKey(r.api_key); setApiKeyLabel(r.label);
        secureStorage.setPref('designsync_api_label', r.label);
      }
    } catch (err) { console.error('Failed to regenerate API key:', err); } finally { setKeyLoading(false); }
  };

  const connectExistingKey = async () => {
    if (!connectKeyInput) return;
    setKeyLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/keys/validate`, {
        headers: { 'Authorization': `Bearer ${connectKeyInput}` },
        credentials: 'include'
      });
      if (res.ok) {
        const d = await res.json();
        if (d.valid) {
          setApiKey(connectKeyInput); setApiKeyLabel(d.label || ''); setUserEmail(d.email || '');
          secureStorage.setPref('designsync_api_label', d.label || '');
          secureStorage.setPref('designsync_user_email', d.email || '');
          setShowConnectKey(false); setConnectKeyInput(''); setAuthMessage('');
        } else { setAuthMessage('Invalid API key.'); }
      } else { setAuthMessage('Invalid API key.'); }
    } catch (err) { console.error('Connection failed:', err); setAuthMessage('Connection failed.'); } finally { setKeyLoading(false); }
  };

  // ──────── Emergent-managed Google sign-in ────────
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return {
    // State
    apiKey, apiKeyLabel, isKeyRevealed, setIsKeyRevealed,
    keyLoading, copySuccess, showRegenerateConfirm, setShowRegenerateConfirm,
    userEmail, emailInput, setEmailInput,
    showEmailPrompt, setShowEmailPrompt,
    otpInput, setOtpInput, showOtpPrompt, setShowOtpPrompt,
    otpEmail, connectKeyInput, setConnectKeyInput,
    rememberMe, setRememberMe,
    showConnectKey, setShowConnectKey,
    authMessage, setAuthMessage,
    // Functions
    clearApiKey, validateSession, validateApiKey,
    maskKey, copyApiKey, generateApiKey, verifyOtp,
    regenerateApiKey, connectExistingKey, loginWithGoogle,
    // Exposed for credential save
    setApiKey, setApiKeyLabel, setUserEmail, secureStorage,
    // Re-export for other modules
    authFetch, API_URL,
  };
}

// Re-export utilities for use in App.js and other modules
export { authFetch, secureStorage, API_URL };
